import { faArrowsUpDown } from "@fortawesome/free-solid-svg-icons/faArrowsUpDown";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons/faEllipsis";
import { faMessage } from "@fortawesome/free-solid-svg-icons/faMessage";
import { faToggleOff } from "@fortawesome/free-solid-svg-icons/faToggleOff";
import { faToggleOn } from "@fortawesome/free-solid-svg-icons/faToggleOn";
import { faHeadset } from "@fortawesome/free-solid-svg-icons/faHeadset";
import type { MenuProps, TableProps } from "../../../utils/local-antd";
import { Tooltip } from "../../../utils/local-antd";
import { TFunction } from "i18next";
import omit from "lodash/omit";
import styled from "styled-components";

import { AtiraDropdown } from "../../../components/AtiraDropdown";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { SearchUsersUserDto } from "../../../model/admin/dto/SearchUsersUserDto";
import { Spacing } from "../../../theme/Spacing";
import { TableActionableCellEmail } from "../../../components/table/TableActionableCellEmail";
import { ThresholdKinds } from "../../../model/threshold/types/ThresholdKinds.enum";

const StyledActionButton = styled(Button)`
  background-color: transparent;
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.main};
`;

type UserFNType = ATVoidFunction<Partial<SearchUsersUserDto>>;

type FieldFunctions = {
  t: TFunction;
  toggleStatus: UserFNType;
  updateThresholds: UserFNType;
  sendMessage: UserFNType;
  signInAsUser: UserFNType;
};

type Column = keyof SearchUsersUserDto | ThresholdKindKey | "actions";

const getFieldWidth = (field: Column) => {
  switch (field) {
    case "actions":
    case "formsCount":
    case "entriesCount":
    case "membersCount":
      return 80;
    case "email":
      return 375;
    case "plan":
      return 120;

    default:
      return 200;
  }
};

const getFieldInfo = (column: Column, functions: FieldFunctions) => {
  const { t } = functions;

  switch (column) {
    case "actions":
      return {
        title: t("common.actions"),
        key: "table-actions",
        render: (data: Partial<SearchUsersUserDto>) => {
          const user = omit(data, [
            "entriesCount",
            "formsCount",
            "membersCount",
          ]) as unknown as Partial<SearchUsersUserDto>;

          const isEnabled = user.enabled === true;

          const items: MenuProps["items"] = [
            {
              label: (
                <StyledActionButton
                  icon={isEnabled ? faToggleOff : faToggleOn}
                  onClick={() => functions.toggleStatus(user)}
                >
                  {t(isEnabled ? "common.disable" : "common.enable")}
                </StyledActionButton>
              ),
            },
            {
              label: (
                <StyledActionButton
                  icon={faArrowsUpDown}
                  onClick={() => functions.updateThresholds(user)}
                >
                  {t("admin.user.action.update_thresholds")}
                </StyledActionButton>
              ),
            },
            {
              label: (
                <StyledActionButton
                  icon={faMessage}
                  onClick={() => functions.sendMessage(user)}
                >
                  {t("common.message")}
                </StyledActionButton>
              ),
            },
            {
              disabled: !data.allowSupportAccess,
              label: (
                <Tooltip
                  title={
                    data.allowSupportAccess
                      ? undefined
                      : t("admin.user.action.sign_in_as_user.not_allowed")
                  }
                >
                  <StyledActionButton
                    icon={faHeadset}
                    disabled={!data.allowSupportAccess}
                    onClick={() => functions.signInAsUser(user)}
                  >
                    {t("admin.user.action.sign_in_as_user")}
                  </StyledActionButton>
                </Tooltip>
              ),
            },
          ].map((x, i) => ({ ...x, key: String(i + 1) }));

          return {
            props: { className: "fixed-cell" },
            children: (
              <Flex justifyContent="center">
                <AtiraDropdown items={items} placement="bottomLeft">
                  <Button
                    icon={faEllipsis}
                    backgroundColor="transparent"
                    margin="none"
                    color="black"
                  />
                </AtiraDropdown>
              </Flex>
            ),
          };
        },

        reorder: true,
        center: true,
        fixed: "left",
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
        data: column,
      };

    case "name":
      return {
        title: t("common.name"),
        key: "table-name",
        data: column,
        render: (text: string, record: SearchUsersUserDto) => {
          const name = record.name;
          return name ? (
            <TextWithTruncated length={20}>{name}</TextWithTruncated>
          ) : (
            ""
          );
        },
        sorter: (a: SearchUsersUserDto, b: SearchUsersUserDto) =>
          a?.name?.localeCompare(b.name),
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "plan":
      return {
        title: t("common.plan"),
        key: "table-plan",
        data: column,
        render: (text: string, record: SearchUsersUserDto) => {
          const plan = record.plan;
          return plan ? (
            <Text>{t(`plan.type.${plan.toLowerCase()}`)}</Text>
          ) : null;
        },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "email":
      return {
        title: t("common.email"),
        key: "table-email",
        data: column,
        render: (text: string, record: SearchUsersUserDto) => {
          const email = record.email;
          return email ? <TableActionableCellEmail email={email} /> : "";
        },
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "ENTRIES":
      return {
        title: t("common.entries"),
        key: "table-entries-count",
        render: (user: SearchUsersUserDto) => {
          return <Text>{user.entriesCount}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "FORMS":
      return {
        title: t("common.forms"),
        key: "table-forms-count",
        render: (user: SearchUsersUserDto) => {
          return <Text>{user.formsCount}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "USERS":
      return {
        title: t("common.members"),
        key: "table-members-count",
        render: (user: SearchUsersUserDto) => {
          return <Text>{user.membersCount}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "CUSTOM_FIELDS":
      return {
        title: t("common.custom_field"),
        key: "table-custom-fields-count",
        render: (user: SearchUsersUserDto) => {
          return <Text>{user.thresholds?.CUSTOM_FIELDS}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "KANBANS":
      return {
        title: t("common.kanbans"),
        key: "table-kanbans-count",
        render: (user: SearchUsersUserDto) => {
          return <Text>{user.thresholds?.KANBANS}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "MODULES_DEFINITIONS":
      return {
        title: t("common.modules_definitions"),
        key: "table-module-definitions-count",
        render: (user: SearchUsersUserDto) => {
          return <Text>{user.thresholds?.MODULES_DEFINITIONS}</Text>;
        },

        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };

    case "createdAt":
      return {
        title: t("common.created"),
        key: "table-createdAt",
        data: column,
        render: (text: string, record: SearchUsersUserDto) => {
          const createdAt = record.createdAt;
          return createdAt ? new Date(createdAt).toLocaleDateString() : "";
        },
        sorter: (a: SearchUsersUserDto, b: SearchUsersUserDto) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(column),
          },
        }),
      };
  }
};

type ThresholdKindKey = keyof typeof ThresholdKinds;

export function createAdminSearchUsersTableColumns<T>(
  functions: FieldFunctions,
) {
  const tableColumnsKeys: Column[] = [
    "actions",
    "name",
    "email",
    "plan",
    "USERS",
    "ENTRIES",
    "FORMS",
    "CUSTOM_FIELDS",
    "KANBANS",
    "MODULES_DEFINITIONS",
    "createdAt",
  ];

  return tableColumnsKeys.map((c) =>
    getFieldInfo(c, functions),
  ) as TableProps<T>["columns"];
}
